import { TabKey } from "../types";

export type FilterableFieldType =
  "TEXT" | "KEYWORD" | "INTEGER" | "LONG" | "DECIMAL" | "DATE" | "BOOLEAN";

export interface FilterableField {
  field: string;
  type: FilterableFieldType;
  operations: string[];
  sortable: boolean;
}

export interface LOSearchCondition {
  field: string;
  operation: string;
  value?: any;
  negate?: boolean;
}

export interface LOSearchOrderBy {
  field: string;
  direction: "asc" | "desc";
}

export const SEARCH_TEXT_MIN_LENGTH = 3;

export interface LOSearchBody {
  searchText?: string;
  conditions: LOSearchCondition[];
  orderBy: LOSearchOrderBy[];
  page: number;
  size: number;
}

export type SearchFilterValues = Record<string, any>;

export const SEARCH_EXCLUDED_FIELDS = [
  "draft",
  "contentType",
  "enrolledUsers",
  "initiativesCount",
  "shortDescription",
];

export const isExcludedSearchField = (field: string): boolean =>
  SEARCH_EXCLUDED_FIELDS.includes(field) || field.endsWith(".keyword");

export const CONTENT_TYPE_BY_TAB: Partial<Record<TabKey, string[]>> = {
  activities: ["DIDACTICAL_ACTIVITY"],
  courses: ["COURSE"],
  paths: ["PATH"],
};

export const STATUS_FILTER_FIELD = "status";

export const STATUS_FILTER_VALUES = [
  "published",
  "draft",
  "publishing",
  "suspended",
];

const todayIso = (): string =>
  `${new Date().toISOString().slice(0, 10)}T00:00:00Z`;

export const buildStatusConditions = (status?: string): LOSearchCondition[] => {
  const today = todayIso();
  switch (status) {
    case "draft":
      return [{ field: "draft", operation: "eq", value: true }];
    case "publishing":
      return [
        { field: "draft", operation: "eq", value: false },
        { field: "publishedAt", operation: "gt", value: today },
      ];
    case "published":
      return [
        { field: "draft", operation: "eq", value: false },
        { field: "publishedAt", operation: "lte", value: today },
        {
          field: "obsolescenceDate",
          operation: "lt",
          value: today,
          negate: true,
        },
      ];
    case "suspended":
      return [{ field: "obsolescenceDate", operation: "lt", value: today }];
    default:
      return [];
  }
};

export const CONTENT_TYPE_FILTER_VALUES = [
  "PATH",
  "COURSE",
  "DIDACTICAL_ACTIVITY",
];

export const SEARCH_FILTER_ORDER_BY_TAB: Record<TabKey, string[]> = {
  all: [
    "id",
    "digitedCode",
    "title",
    "contentType",
    "activityType",
    "standalone",
    "status",
    "publishedAt",
    "categories",
    "topics",
  ],
  activities: [
    "id",
    "digitedCode",
    "title",
    "activityType",
    "standalone",
    "status",
    "publishedAt",
    "categories",
    "topics",
  ],
  courses: [
    "id",
    "digitedCode",
    "title",
    "standalone",
    "status",
    "publishedAt",
    "categories",
    "topics",
  ],
  paths: [
    "id",
    "digitedCode",
    "title",
    "status",
    "publishedAt",
    "categories",
    "topics",
  ],
};

export const LO_SORT_ORDER_BY: Record<string, LOSearchOrderBy[]> = {
  "id-desc": [{ field: "id", direction: "desc" }],
  "most-recent": [{ field: "publishedAt", direction: "desc" }],
  "least-recent": [{ field: "publishedAt", direction: "asc" }],
  "alphabetical-az": [{ field: "title", direction: "asc" }],
  "alphabetical-za": [{ field: "title", direction: "desc" }],
};

export const LO_SORT_OPTION_KEYS = [
  "id-desc",
  "most-recent",
  "least-recent",
  "alphabetical-az",
  "alphabetical-za",
];

export const ACTIVITY_TYPE_VALUES = [
  "DA_XAPI",
  "DA_SCORM",
  "DA_PHYSICAL_CLASS",
  "DA_VIRTUAL_CLASS",
  "DA_EXTERNAL_RES",
  "DA_YOUTUBE",
  "DA_LINKEDIN",
  "DA_LTI",
  "DA_MEETING",
  "DA_URL_RES",
  "DA_SURVEY",
  "DA_MATERIAL",
  "DA_GOALS",
  "DA_FINAL_BALANCE",
];

const isEmpty = (value: any): boolean =>
  value === undefined || value === null || value === "";

export const buildSearchConditions = ({
  contentTypes,
  includeDrafts = true,
  standaloneOnly = false,
  excludeObsolete = false,
  fields,
  filterValues,
}: {
  contentTypes?: string[];
  includeDrafts?: boolean;
  standaloneOnly?: boolean;
  excludeObsolete?: boolean;
  fields: FilterableField[];
  filterValues: SearchFilterValues;
}): LOSearchCondition[] => {
  const conditions: LOSearchCondition[] = [];

  if (!includeDrafts) {
    conditions.push({ field: "draft", operation: "eq", value: false });
  }

  if (standaloneOnly) {
    conditions.push({ field: "standalone", operation: "eq", value: true });
  }

  // se filtro stato = "Sospeso" ignoriamo l'excludeObsolete.
  if (excludeObsolete && filterValues[STATUS_FILTER_FIELD] !== "suspended") {
    conditions.push({
      field: "obsolescenceDate",
      operation: "lte",
      value: todayIso(),
      negate: true,
    });
  }

  if (contentTypes && contentTypes.length > 0) {
    conditions.push({
      field: "contentType",
      operation: "in",
      value: contentTypes,
    });
  } else {
    const rawContentType = filterValues["contentType"];
    if (Array.isArray(rawContentType) && rawContentType.length > 0) {
      conditions.push({
        field: "contentType",
        operation: "in",
        value: rawContentType,
      });
    }
  }

  conditions.push(...buildStatusConditions(filterValues[STATUS_FILTER_FIELD]));

  fields.forEach((fieldDef) => {
    if (isExcludedSearchField(fieldDef.field)) return;
    const raw = filterValues[fieldDef.field];
    if (raw === undefined || raw === null) return;

    switch (fieldDef.type) {
      case "TEXT": {
        if (!isEmpty(raw)) {
          conditions.push({
            field: fieldDef.field,
            operation: "match",
            value: String(raw),
          });
        }
        break;
      }
      case "KEYWORD": {
        if (Array.isArray(raw)) {
          if (raw.length > 0) {
            conditions.push({
              field: fieldDef.field,
              operation: "in",
              value: raw,
            });
          }
        } else if (!isEmpty(raw)) {
          conditions.push({
            field: fieldDef.field,
            operation: "eq",
            value: String(raw),
          });
        }
        break;
      }
      case "INTEGER":
      case "LONG":
      case "DECIMAL": {
        if (typeof raw !== "object") {
          if (!isEmpty(raw)) {
            conditions.push({
              field: fieldDef.field,
              operation: "eq",
              value: Number(raw),
            });
          }
          break;
        }
        const { min, max } = raw || {};
        if (!isEmpty(min)) {
          conditions.push({
            field: fieldDef.field,
            operation: "gte",
            value: Number(min),
          });
        }
        if (!isEmpty(max)) {
          conditions.push({
            field: fieldDef.field,
            operation: "lte",
            value: Number(max),
          });
        }
        break;
      }
      case "DATE": {
        if (typeof raw === "string") {
          if (!isEmpty(raw)) {
            const day = raw.slice(0, 10);
            conditions.push({
              field: fieldDef.field,
              operation: "gte",
              value: day,
            });
            conditions.push({
              field: fieldDef.field,
              operation: "lte",
              value: day,
            });
          }
          break;
        }
        const { from, to } = raw || {};
        if (!isEmpty(from)) {
          conditions.push({
            field: fieldDef.field,
            operation: "gte",
            value: String(from).slice(0, 10),
          });
        }
        if (!isEmpty(to)) {
          conditions.push({
            field: fieldDef.field,
            operation: "lte",
            value: String(to).slice(0, 10),
          });
        }
        break;
      }
      case "BOOLEAN": {
        if (raw === "true" || raw === true) {
          conditions.push({
            field: fieldDef.field,
            operation: "eq",
            value: true,
          });
        } else if (raw === "false" || raw === false) {
          conditions.push({
            field: fieldDef.field,
            operation: "eq",
            value: false,
          });
        }
        break;
      }
      default:
        break;
    }
  });

  return conditions.slice(0, 20);
};
